import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, SunIcon } from '@heroicons/react/24/outline';
import { ToggleSwitch } from '@/ui/components/atoms/ToggleSwitch/ToggleSwitch';
import { useAuth } from '@/core/auth';
import { useTeamPlanning, useWeekNavigation } from './hooks/useTeamPlanning';
import { TeamCalendar } from './components/TeamCalendar';
import { EquipeCongesTab } from './components/EquipeCongesTab';
import './EquipeTab.css';

type EquipeView = 'planning' | 'conges';

export const EquipeTab: React.FC = () => {
  // Both planning and Congés sub-views are available to admin + bureau.
  // Bureau gets a READ-ONLY Congés view (no approve/deny, no config editing) —
  // those actions stay admin-gated client- and server-side.
  const { isAdmin } = useAuth();
  const [view, setView] = useState<EquipeView>('planning');
  const {
    getCurrentMonday,
    formatDate,
    getWeekDates,
    getWeekRangeString,
    isCurrentWeek
  } = useWeekNavigation();

  // State for current week
  const [currentMonday, setCurrentMonday] = useState<Date>(() => getCurrentMonday());

  // Computed values
  const weekStart = useMemo(() => formatDate(currentMonday), [currentMonday, formatDate]);
  const weekDates = useMemo(() => getWeekDates(currentMonday), [currentMonday, getWeekDates]);
  const weekRangeString = useMemo(() => getWeekRangeString(currentMonday), [currentMonday, getWeekRangeString]);
  const isCurrent = useMemo(() => isCurrentWeek(currentMonday), [currentMonday, isCurrentWeek]);

  // Fetch team planning
  const { teamPlanning, isLoading, error, refetch } = useTeamPlanning({ weekStart });

  // Week navigation handlers
  const goToPreviousWeek = () => {
    const newMonday = new Date(currentMonday);
    newMonday.setDate(newMonday.getDate() - 7);
    setCurrentMonday(newMonday);
  };

  const goToNextWeek = () => {
    const newMonday = new Date(currentMonday);
    newMonday.setDate(newMonday.getDate() + 7);
    setCurrentMonday(newMonday);
  };

  const goToCurrentWeek = () => {
    setCurrentMonday(getCurrentMonday());
  };

  // Count available members for current moment
  const availableNow = useMemo(() => {
    if (!teamPlanning.length) return 0;

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    const todayDate = format(now, 'yyyy-MM-dd');

    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayDay = days[now.getDay()];

    return teamPlanning.filter((member) => {
      const dayPlanning = member.planning[todayDay as keyof typeof member.planning];
      if (!dayPlanning?.enabled) return false;

      // Check if current time is within working hours
      const [startH, startM] = dayPlanning.start.split(':').map(Number);
      const [endH, endM] = dayPlanning.end.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;

      if (currentTimeMinutes < startMinutes || currentTimeMinutes > endMinutes) {
        return false;
      }

      // Check if they have an appointment now
      const hasAppointmentNow = member.appointments.some((apt) => {
        if (apt.date !== todayDate) return false;
        const [aptStartH, aptStartM] = apt.startTime.split(':').map(Number);
        const [aptEndH, aptEndM] = apt.endTime.split(':').map(Number);
        const aptStart = aptStartH * 60 + aptStartM;
        const aptEnd = aptEndH * 60 + aptEndM;
        return currentTimeMinutes >= aptStart && currentTimeMinutes <= aptEnd;
      });

      return !hasAppointmentNow;
    }).length;
  }, [teamPlanning]);

  return (
    <div className="equipe-page-container">
      {/* Page Header */}
      <div className="equipe-header">
        <div className="equipe-header-content">
          <div className="equipe-title-row">
            <div>
              <h1 className="equipe-title">Équipe</h1>
              <p className="equipe-subtitle">
                {view === 'planning'
                  ? "Planning de l'équipe - Consultez les disponibilités"
                  : isAdmin
                    ? "Congés équipe - Approbations et configuration"
                    : "Congés équipe - Vue d'ensemble"}
              </p>
            </div>

            <div className="equipe-toggle-wrapper">
              <ToggleSwitch
                options={[
                  { value: 'planning', label: 'Planning', icon: <CalendarIcon style={{ width: 16, height: 16 }} /> },
                  { value: 'conges', label: 'Congés', icon: <SunIcon style={{ width: 16, height: 16 }} /> },
                ]}
                value={view}
                onChange={v => setView(v as EquipeView)}
                size="medium"
                colorScheme="default"
                aria-label="Vue Équipe"
              />
            </div>

            {/* Available now badge — planning view only */}
            {view === 'planning' && isCurrent && teamPlanning.length > 0 && (
              <div className="equipe-available-badge">
                <span className="available-dot" />
                <span className="available-text">
                  {availableNow} / {teamPlanning.length} disponible{availableNow !== 1 ? 's' : ''} maintenant
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {view === 'conges' && (
        <div className="equipe-content">
          <EquipeCongesTab canManage={isAdmin} />
        </div>
      )}

      {view === 'planning' && (<>

      {/* Week Navigation */}
      <div className="equipe-controls">
        <div className="equipe-week-selector">
          <button
            className="equipe-nav-button"
            onClick={goToPreviousWeek}
            title="Semaine précédente"
          >
            <ChevronLeftIcon className="nav-icon" />
          </button>

          <div className="equipe-week-display">
            <CalendarIcon className="calendar-icon" />
            <span className="week-range">{weekRangeString}</span>
          </div>

          <button
            className="equipe-nav-button"
            onClick={goToNextWeek}
            title="Semaine suivante"
          >
            <ChevronRightIcon className="nav-icon" />
          </button>

          {!isCurrent && (
            <button
              className="equipe-today-button"
              onClick={goToCurrentWeek}
            >
              Aujourd'hui
            </button>
          )}
        </div>

        <button
          className="equipe-refresh-button"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          {isLoading ? 'Chargement...' : 'Actualiser'}
        </button>
      </div>

      {/* Content */}
      <div className="equipe-content">
        {isLoading ? (
          <div className="equipe-loading">
            <div className="equipe-spinner" />
            <p>Chargement du planning de l'équipe...</p>
          </div>
        ) : error ? (
          <div className="equipe-error">
            <p>Erreur lors du chargement du planning</p>
            <button onClick={() => refetch()}>Réessayer</button>
          </div>
        ) : teamPlanning.length === 0 ? (
          <div className="equipe-empty">
            <div className="equipe-empty-icon">👥</div>
            <h3>Aucun membre de l'équipe</h3>
            <p>Il n'y a pas encore de membres admin ou bureau avec un planning configuré.</p>
          </div>
        ) : (
          <TeamCalendar
            teamPlanning={teamPlanning}
            weekDates={weekDates}
            currentWeekStart={weekStart}
          />
        )}
      </div>
      </>)}
    </div>
  );
};

export default EquipeTab;
